import { Grid, Paper, Tab, Tabs, Box, useTheme } from '@mui/material';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { checkOverFlow } from '../helpers/overflow';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ px: 2, py: 1 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const GenTabs = ({ tabs, button = null, currentTab = () => {} }) => {
  const theme = useTheme();
  const [value, setValue] = useState(0);
  const [element, setElement] = useState(null);
  const [overFlow, setOverFlow] = useState(false);
  const handleChange = (event, newValue) => {
    setValue(newValue);
    currentTab(newValue);
  };

  const tabsElement = useCallback(node => {
    if (node) setElement(node?.children[1]?.children[0]);
  }, []);

  useEffect(() => {
    if (element) {
      setOverFlow(checkOverFlow(element));
    }
  }, [element]);

  useEffect(() => {
    const debouncedHandleResize = debounce(() => {
      if (element) {
        setOverFlow(checkOverFlow(element));
      }
    }, 500);

    window.addEventListener('resize', debouncedHandleResize);

    return _ => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  });

  return (
    <Grid container sx={{ position: 'relative' }}>
      <Grid item sx={{ width: '100%' }}>
        <Paper
          square
          sx={{ flexGrow: 1, width: '100%', boxShadow: theme.shadows[0] }}
        >
          <Box>
            <Tabs
              ref={tabsElement}
              value={value}
              onChange={handleChange}
              indicatorColor="primary"
              textColor="primary"
              aria-label="tabs filter"
              data-test="tabs"
              variant="scrollable"
              scrollButtons={false}
              sx={{
                position: 'relative',
                ...(overFlow && {
                  '&::before': {
                    content: '""',
                    display: 'block',
                    width: '40px',
                    height: '100%',
                    background: 'linear-gradient(90deg, white, transparent)',
                    position: 'absolute',
                    left: '0px',
                    zIndex: 10,
                    pointerEvents: 'none',
                  },
                  '&::after': {
                    content: '""',
                    display: 'block',
                    width: '40px',
                    height: '100%',
                    background: 'linear-gradient(-90deg, white, transparent)',
                    position: 'absolute',
                    right: '0px',
                    zIndex: 10,
                    pointerEvents: 'none',
                  },
                }),
              }}
            >
              {Object.keys(tabs).map((t, i) => (
                <Tab
                  key={`tab${i}`}
                  label={tabs[t].name}
                  icon={tabs[t].icon}
                  {...a11yProps(i)}
                  sx={{
                    flexDirection: 'column',
                    flexGrow: 1,
                    [theme.breakpoints.up('lg')]: {
                      height: '56px',
                      minHeight: 'auto',
                      flexDirection: 'row',
                      '& .MuiSvgIcon-root': {
                        marginRight: theme.spacing(1),
                        marginBottom: theme.spacing(0),
                      },
                    },
                  }}
                />
              ))}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  marginLeft: 'auto',
                  paddingRight: theme.spacing(2),
                }}
              >
                {Object.values(tabs)[value]?.button || button}
              </Box>
            </Tabs>
          </Box>
        </Paper>
        {Object.keys(tabs).map((t, i) => (
          <TabPanel
            key={`tabPanel${i}`}
            value={value}
            index={i}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              [theme.breakpoints.up('sm')]: {
                padding: theme.spacing(1),
              },
            }}
          >
            {tabs[t].component}
          </TabPanel>
        ))}
      </Grid>
    </Grid>
  );
};

export default GenTabs;
